import {
  Activity,
  IWhitelistedUser,
  TActionPlan,
  TForm,
  TSession,
  TSpace,
  TViewSettings,
} from '@valuecase/common'
import { createContext, ReactNode, useContext } from 'react'

export interface SpaceSidebarDataContextValue {
  space: TSpace | null
  onCheckTask: (taskId: string, checked: boolean) => Promise<void>
  onOpenBlock: (props: { spaceId: string; blockId?: string; pageId?: string }) => void
  whitelistedUsers?: IWhitelistedUser[] | null
  actionPlans: {
    data: TActionPlan[]
    isLoading: boolean
    error: Error | null
  }
  comments: {
    data: any[]
    isLoading: boolean
    error: Error | null
  }
  activities: {
    data: {
      activities: Activity[]
      sessions: TSession[]
    }
    isLoading: boolean
    error: Error | null
  }
  forms: {
    data: TForm[]
    isLoading: boolean
    error: Error | null
  }
  unreadActivitiesCount: number
  unreadCommentsCount: number
  onMarkActivitiesAsRead: () => void
  onMarkCommentsAsRead: () => Promise<void>
  onOpenSpace: () => void
  onCopySpaceLink: () => void
  contextMenu: ReactNode
  getBlockTitle: (data: any) => string | undefined
  viewSettings: TViewSettings | undefined
  onUpdateViewSettings: (settings: Partial<TViewSettings['settings']>) => Promise<void>
}

export const SpaceSidebarDataContext = createContext<SpaceSidebarDataContextValue | null>(null)

export interface SpaceSidebarDataProviderPRops {
  children: ReactNode
  value: SpaceSidebarDataContextValue
}

/**
 * This provider allows us to pass in data and callbacks to the space sidebar and its nested components.
 * Because the sidebar is used in seller-frontend and chrome-extension, we need to abstract the data and callbacks
 * into a single provider to avoid prop drilling as in both instances the callbacks will do different things and the data will be fetched differently.
 */
export const SpaceSidebarDataProvider = ({ children, value }: SpaceSidebarDataProviderPRops) => {
  return (
    <SpaceSidebarDataContext.Provider value={value}>{children}</SpaceSidebarDataContext.Provider>
  )
}

export const useSpaceSidebar = (): SpaceSidebarDataContextValue => {
  const context = useContext(SpaceSidebarDataContext)

  if (!context) {
    throw new Error('useSpaceSheet must be used within a SpaceSheetProvider')
  }

  return context
}
